import React, { useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { anadirActividad, leerPropuestaActividad, mensajeError, nuevoIdPeticion } from '../../api/feriasApi';
import type { OpcionActividad, PropuestaActividad, ValoresActividad } from '../../api/feriasApi';
import { FctAviso } from '../../fct/react';
import { motivoEnPantalla } from './motivos';

/** El formulario: los valores de la actividad, con el inicio como fecha del selector. */
type Formulario = Omit<ValoresActividad, 'inicio'> & { inicio: Dayjs };

const opciones = (lista: OpcionActividad[]) => lista.map((o) => ({ value: o.id, label: o.nombre }));

interface Props {
  idCaso: number;
  /** El cliente del caso, como lo muestra la cabecera: solo informativo, no se edita. */
  cliente: string;
  /** Tras guardarse en Gescom: la pantalla vuelve a leer la actividad del cliente y la cabecera. */
  onCreada: (idActividad: number) => void;
}

/**
 * «Añadir actividad» (M6 §3.1, punto 3; D-FE-40): un formulario emergente casi relleno con los valores de la
 * tabla de §3.1, en el que normalmente el supervisor solo retoca la reseña y elige el protocolo, obligatorio
 * (D13), de una lista filtrada por tipo de actividad y tipo de evento. El responsable es el supervisor y el
 * cliente el del caso, y no se editan; sin ficha de empleado, la pantalla lo avisa antes de abrir el
 * formulario. Se pueden añadir varias (P2); un doble clic o un reintento no crean dos (D39).
 */
export const AnadirActividad: React.FC<Props> = ({ idCaso, cliente, onCreada }) => {
  const [form] = Form.useForm<Formulario>();
  const [propuesta, setPropuesta] = useState<PropuestaActividad | null>(null);
  const [abierta, setAbierta] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardada, setGuardada] = useState<number | null>(null);
  // Una petición por formulario abierto: un reintento tras un fallo de red no crea otra actividad (D39).
  const peticion = useRef<string | null>(null);
  const enCurso = useRef(false);

  const idTipoActividad = Form.useWatch('idTipoActividad', form);
  const idTipoEvento = Form.useWatch('idTipoEvento', form);
  const protocolos = (propuesta?.catalogos.protocolos ?? []).filter(
    (p) => p.idTipoEvento === idTipoEvento && (p.idTipoActividad === null || p.idTipoActividad === idTipoActividad),
  );

  // La propuesta, cada vez que se abre: valores frescos y el responsable comprobado otra vez.
  const abrir = async () => {
    setAviso(null);
    setError(null);
    setGuardada(null);
    setCargando(true);
    try {
      const leida = await leerPropuestaActividad(idCaso);
      if (!leida.responsable) {
        setAviso('Su usuario no tiene ficha de empleado en Gescom, y la actividad necesita un responsable. Pídala a Sistemas.');
        return;
      }
      setPropuesta(leida);
      peticion.current = null;
      form.setFieldsValue({ ...leida.valores, inicio: dayjs(leida.valores.inicio) });
      setAbierta(true);
    } catch (e) {
      setAviso(motivoEnPantalla(mensajeError(e)));
    } finally {
      setCargando(false);
    }
  };

  // Con otro tipo de actividad o de evento, el protocolo elegido puede dejar de valer: se vacía.
  const alCambiarFiltro = () => {
    const elegido = form.getFieldValue('idProtocolo');
    const tipo = form.getFieldValue('idTipoActividad');
    const evento = form.getFieldValue('idTipoEvento');
    const sigueValiendo = propuesta?.catalogos.protocolos.some(
      (p) => p.id === elegido && p.idTipoEvento === evento && (p.idTipoActividad === null || p.idTipoActividad === tipo),
    );
    if (!sigueValiendo) {
      form.setFieldsValue({ idProtocolo: null });
    }
  };

  const guardar = async () => {
    const valores = await form.validateFields().catch(() => null);
    if (!valores || enCurso.current) {
      return;
    }
    enCurso.current = true;
    setGuardando(true);
    setError(null);
    peticion.current = peticion.current ?? nuevoIdPeticion();
    try {
      const anadida = await anadirActividad(idCaso, { ...valores, inicio: valores.inicio.format(), resenia: valores.resenia.trim() }, peticion.current);
      setAbierta(false);
      setGuardada(anadida.idActividad);
      onCreada(anadida.idActividad);
    } catch (e) {
      setError(motivoEnPantalla(mensajeError(e)));
    } finally {
      enCurso.current = false;
      setGuardando(false);
    }
  };

  return (
    <>
      {aviso && <FctAviso tipo="aviso" titulo={aviso} />}
      {guardada !== null && <FctAviso tipo="exito" titulo={`Actividad nº ${guardada} guardada en Gescom`} />}
      <Button loading={cargando} onClick={() => void abrir()}>
        Añadir actividad
      </Button>
      <Modal
        open={abierta}
        title="Añadir actividad"
        width={720}
        okText="Guardar en Gescom"
        cancelText="Cancelar"
        okButtonProps={{ loading: guardando }}
        onOk={() => void guardar()}
        onCancel={() => setAbierta(false)}
        // El formulario sigue montado cerrado: así recibe los valores de la propuesta antes de abrirse.
        forceRender
      >
        {error && (
          <FctAviso tipo="error" titulo="No se ha guardado la actividad">
            {error}
          </FctAviso>
        )}
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>
            <b>Responsable:</b> {propuesta?.responsable?.nombre} · <b>Cliente:</b> {cliente}
          </span>
          <Form form={form} layout="vertical" requiredMark="optional">
            <Form.Item name="idTipoActividad" label="Tipo de actividad" rules={[{ required: true, message: 'El tipo de actividad es obligatorio.' }]}>
              <Select options={opciones(propuesta?.catalogos.tiposActividad ?? [])} onChange={alCambiarFiltro} />
            </Form.Item>
            <Form.Item name="idTipoEvento" label="Tipo de evento" rules={[{ required: true, message: 'El tipo de evento es obligatorio.' }]}>
              <Select options={opciones(propuesta?.catalogos.tiposEvento ?? [])} onChange={alCambiarFiltro} />
            </Form.Item>
            <Form.Item name="idProtocolo" label="Protocolo" rules={[{ required: true, message: 'Elija el protocolo.' }]}>
              <Select placeholder="Elija el protocolo" options={protocolos.map((p) => ({ value: p.id, label: p.nombre }))} />
            </Form.Item>
            <Space wrap>
              <Form.Item name="inicio" label="Inicio" rules={[{ required: true, message: 'El inicio es obligatorio.' }]}>
                <DatePicker showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" />
              </Form.Item>
              <Form.Item name="duracionMinutos" label="Duración (minutos)" rules={[{ required: true, message: 'La duración es obligatoria.' }]}>
                <InputNumber min={1} />
              </Form.Item>
            </Space>
            <Form.Item name="idModalidad" label="Modalidad" rules={[{ required: true, message: 'La modalidad es obligatoria.' }]}>
              <Select options={opciones(propuesta?.catalogos.modalidades ?? [])} />
            </Form.Item>
            <Form.Item name="idUbicacion" label="Ubicación" rules={[{ required: true, message: 'La ubicación es obligatoria.' }]}>
              <Select placeholder="Elija la ubicación" options={opciones(propuesta?.catalogos.ubicaciones ?? [])} />
            </Form.Item>
            <Form.Item name="idEstado" label="Estado" rules={[{ required: true, message: 'El estado es obligatorio.' }]}>
              <Select options={opciones(propuesta?.catalogos.estados ?? [])} />
            </Form.Item>
            <Form.Item name="resenia" label="Reseña" rules={[{ required: true, whitespace: true, message: 'La reseña es obligatoria.' }]}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  );
};
